import React, { useState } from 'react';
import { PhonemeStats } from '../utils/phonemeAnalysis';
import PhonemeExamplePlayer from './PhonemeExamplePlayer';
import PhonemePractice from './PhonemePractice';
import './PracticeRecommendations.css';

interface PracticeRecommendationsProps {
  phonemeStats: PhonemeStats;
  compact?: boolean;
}

const PracticeRecommendations: React.FC<PracticeRecommendationsProps> = ({ phonemeStats, compact = false }) => {
  const [practicePhoneme, setPracticePhoneme] = useState<string | null>(null);
  
  const getClarityGrade = (clarity: number): { grade: string; color: string; description: string } => {
    if (clarity >= 0.8) return { grade: 'A', color: '#4CAF50', description: 'Excellent' };
    if (clarity >= 0.7) return { grade: 'B', color: '#8BC34A', description: 'Good' };
    if (clarity >= 0.6) return { grade: 'C', color: '#FFC107', description: 'Fair' };
    if (clarity >= 0.5) return { grade: 'D', color: '#FF9800', description: 'Needs Work' };
    return { grade: 'F', color: '#F44336', description: 'Poor' };
  };

  const clarityGrade = getClarityGrade(phonemeStats.clarity);

  // Extract phonemes from recommendations text
  const extractPhonemes = (text: string): { phoneme: string; description: string }[] => {
    // Match patterns like /p/, /t/, /k/ or specific sounds
    const phonemePattern = /\/[^\/]+\//g;
    const matches = text.match(phonemePattern) || [];
    
    const phonemes: { phoneme: string; description: string }[] = [];
    matches.forEach(match => {
      if (!phonemes.find(p => p.phoneme === match)) {
        phonemes.push({ phoneme: match, description: text });
      }
    });
    
    return phonemes;
  };

  // Render recommendation with clickable phonemes
  const renderRecommendationText = (text: string) => {
    const phonemes = extractPhonemes(text);
    let processedText = text;
    
    // Replace phonemes with placeholders
    phonemes.forEach((p, index) => {
      processedText = processedText.replace(p.phoneme, `__PHONEME_${index}__`);
    });
    
    // Split and rebuild with buttons
    const parts = processedText.split(/__PHONEME_\d+__/);
    const result: React.ReactElement[] = [];
    
    parts.forEach((part, i) => {
      result.push(<span key={`text-${i}`}>{part}</span>);
      if (i < phonemes.length) {
        result.push(
          <button
            key={`phoneme-${i}`}
            className="phoneme-practice-btn"
            onClick={() => setPracticePhoneme(phonemes[i].phoneme)}
            title={`Practice ${phonemes[i].phoneme}`}
          >
            {phonemes[i].phoneme} 🎯
          </button>
        );
      }
    });
    
    return <>{result}</>;
  };

  // In compact mode, show only the essential recommendations
  if (compact) {
    return (
      <div className="practice-recommendations compact">
        {practicePhoneme && (
          <PhonemePractice
            phoneme={practicePhoneme}
            onClose={() => setPracticePhoneme(null)}
          />
        )}
        
        {phonemeStats.difficultPhonemes.length > 0 && (
          <div className="difficult-phonemes">
            <h4>Phonemes to Practice</h4>
            <div className="phoneme-tags">
              {phonemeStats.difficultPhonemes.map((phoneme, index) => (
                <div key={index} className="phoneme-tag-container">
                  <span className="phoneme-tag">
                    {phoneme}
                  </span>
                  <PhonemeExamplePlayer phoneme={phoneme} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="recommendations-list compact">
          <h4>Practice Recommendations</h4>
          <div className="recommendations">
            {phonemeStats.recommendations.slice(0, 3).map((recommendation, index) => (
              <div key={index} className="recommendation-item">
                <span className="recommendation-number">{index + 1}</span>
                <span className="recommendation-text">
                  {renderRecommendationText(recommendation)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="practice-recommendations">
      {practicePhoneme && (
        <PhonemePractice
          phoneme={practicePhoneme}
          onClose={() => setPracticePhoneme(null)}
        />
      )}
      <h3 className="recommendations-title">🎯 Speech Analysis & Coaching</h3>
      
      <div className="phoneme-overview">
        <div className="phoneme-chart">
          <div className="chart-section vowels">
            <span className="chart-label">Vowels</span>
            <div className="chart-bar">
              <div 
                className="chart-fill vowel-fill" 
                style={{ width: `${phonemeStats.vowelPercentage}%` }}
              />
            </div>
            <span className="chart-value">{phonemeStats.vowelPercentage}%</span>
          </div>
          
          <div className="chart-section consonants">
            <span className="chart-label">Consonants</span>
            <div className="chart-bar">
              <div 
                className="chart-fill consonant-fill" 
                style={{ width: `${phonemeStats.consonantPercentage}%` }}
              />
            </div>
            <span className="chart-value">{phonemeStats.consonantPercentage}%</span>
          </div>
        </div>
        
        <div className="clarity-score">
          <div className="clarity-grade" style={{ backgroundColor: clarityGrade.color }}>
            {clarityGrade.grade}
          </div>
          <div className="clarity-info">
            <div className="clarity-label">Speech Clarity</div>
            <div className="clarity-description">{clarityGrade.description}</div>
            <div className="clarity-percentage">{Math.round(phonemeStats.clarity * 100)}%</div>
          </div>
        </div>
      </div>

      <div className="consonant-breakdown">
        <h4>Consonant Pattern Analysis</h4>
        <div className="consonant-types">
          <div className="consonant-type">
            <span className="type-icon">💨</span>
            <span className="type-label">Fricatives</span>
            <span className="type-count">{phonemeStats.fricativeCount}</span>
            <span className="type-examples">(/s/, /z/, /f/, /v/, /θ/, /ð/, /ʃ/, /ʒ/)</span>
          </div>
          <div className="consonant-type">
            <span className="type-icon">💥</span>
            <span className="type-label">Stops</span>
            <span className="type-count">{phonemeStats.stopCount}</span>
            <span className="type-examples">(/p/, /t/, /k/, /b/, /d/, /g/)</span>
          </div>
          <div className="consonant-type">
            <span className="type-icon">👃</span>
            <span className="type-label">Nasals</span>
            <span className="type-count">{phonemeStats.nasalCount}</span>
            <span className="type-examples">(/m/, /n/, /ŋ/)</span>
          </div>
        </div>
      </div>

      <div className="formant-analysis">
        <h4>Voice Characteristics (Formants)</h4>
        <div className="formant-display">
          <div className="formant-item">
            <span className="formant-label">F1</span>
            <span className="formant-value">{phonemeStats.dominantFormants.f1} Hz</span>
            <span className="formant-description">Vowel openness</span>
          </div>
          <div className="formant-item">
            <span className="formant-label">F2</span>
            <span className="formant-value">{phonemeStats.dominantFormants.f2} Hz</span>
            <span className="formant-description">Tongue position</span>
          </div>
          <div className="formant-item">
            <span className="formant-label">F3</span>
            <span className="formant-value">{phonemeStats.dominantFormants.f3} Hz</span>
            <span className="formant-description">Voice quality</span>
          </div>
        </div>
      </div>

      {phonemeStats.difficultPhonemes.length > 0 && (
        <div className="difficult-phonemes">
          <h4>Areas for Improvement</h4>
          <div className="phoneme-tags">
            {phonemeStats.difficultPhonemes.map((phoneme, index) => (
              <div key={index} className="phoneme-tag-container">
                <span className="phoneme-tag">
                  {phoneme}
                </span>
                <PhonemeExamplePlayer phoneme={phoneme} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="recommendations-list">
        <h4>🎯 Personalized Practice Recommendations</h4>
        <p className="practice-hint">Click on any phoneme (e.g., /s/, /p/) to practice it!</p>
        <div className="recommendations">
          {phonemeStats.recommendations.map((recommendation, index) => (
            <div key={index} className="recommendation-item">
              <span className="recommendation-number">{index + 1}</span>
              <span className="recommendation-text">
                {renderRecommendationText(recommendation)}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="practice-tips">
        <h4>💡 Quick Practice Tips</h4>
        <div className="tips-grid">
          <div className="tip-card">
            <span className="tip-icon">🗣️</span>
            <span className="tip-title">Daily Practice</span>
            <span className="tip-description">Record yourself for 2-3 minutes daily</span>
          </div>
          <div className="tip-card">
            <span className="tip-icon">👂</span>
            <span className="tip-title">Listen Back</span>
            <span className="tip-description">Compare your recordings over time</span>
          </div>
          <div className="tip-card">
            <span className="tip-icon">📚</span>
            <span className="tip-title">Read Aloud</span>
            <span className="tip-description">Practice with books or articles</span>
          </div>
          <div className="tip-card">
            <span className="tip-icon">🎵</span>
            <span className="tip-title">Rhythm</span>
            <span className="tip-description">Focus on natural speech rhythm</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PracticeRecommendations;